import { ReactElement } from "react";

import { useSelector } from "react-redux";
import {
  AddressCompleteSearchProvider,
  AddressFieldWithMelissaConfig,
  CountryFieldConfig,
} from "@evergiving/common/dist/es2015/form";
import { notNil } from "@evergiving/common/dist/commonjs/typescriptUtils";

import { RootState } from "@/lib/redux/types";
import {
  MelissaPostalAddress,
  PostalAddressKind,
} from "@evergiving/common/dist/es2015/pledge";
import { SearchAddressField } from "@/lib/components/SearchAddressField";
import { SearchAddressWithProviderField } from "@/lib/components/SearchAddressWithProviderField";
import { FreeFormAddressComponent } from "@/lib/components/FreeFormAddressComponent";

import { useAddressFieldWithSearch } from "../hooks/useAddressFieldWithSearch";

export function AddressFieldWithMelissaSearch({
  locale,
  countryFieldConfig,
  addressFieldConfig,
  defaultCountryCode,
}: {
  locale: string;
  countryFieldConfig: CountryFieldConfig | undefined;
  addressFieldConfig: AddressFieldWithMelissaConfig;
  defaultCountryCode: string;
}): ReactElement | null {
  const postalAddress = useSelector((state: RootState) => state.postalAddress);

  const searchUrl = new URL(
    addressFieldConfig.melissa.addressCompletionEndpointUrl
  );

  const {
    handleChange,
    handleSearch,
    handleClear,
    countryName,
    freeFormAddressFieldConfig,
    results,
    loading,
    text,
  } = useAddressFieldWithSearch({
    searchUrl,
    searchProvider: AddressCompleteSearchProvider.Melissa,
    countryFieldConfig,
    addressFieldWithoutSearch: addressFieldConfig,
  });

  if (postalAddress === undefined) {
    return (
      <SearchAddressField
        locale={locale}
        addressFieldConfig={addressFieldConfig}
        results={results}
        onChange={handleChange}
        onSearch={handleSearch}
        onClear={handleClear}
        loading={loading}
        value={text}
        defaultCountryCode={defaultCountryCode}
      />
    );
  }

  if (postalAddress.kind === PostalAddressKind.Melissa) {
    const addressLineFields = [
      "Address1",
      "Address2",
      "Address3",
      "Address4",
      "Address5",
      "Address6",
      "Address7",
      "Address8",
      "Address9",
      "Address10",
      "Address11",
      "Address12",
    ] as (keyof MelissaPostalAddress)[];

    const addressLine = addressLineFields
      .map((field) => postalAddress[field])
      .filter(notNil)
      .filter((field) => String(field).length > 0)
      .concat(countryName === undefined ? [] : [countryName])
      .join("\n");

    return (
      <SearchAddressWithProviderField
        addressLine={addressLine}
        searchAddressProviderFieldConfig={addressFieldConfig.melissa}
      />
    );
  }

  return (
    <FreeFormAddressComponent
      freeFormAddressFieldConfig={freeFormAddressFieldConfig}
    />
  );
}
